import { useEffect, useState } from "react";
import { BookOpen, PlayCircle, Headphones, Download, Video } from "lucide-react";
import { portalApi, mediaUrl } from "@/lib/portalApi";

function Teaching({ url }) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (yt) return <iframe title="teaching" className="w-full aspect-video rounded-lg" src={`https://www.youtube.com/embed/${yt[1]}`} allowFullScreen />;
  if (vimeo) return <iframe title="teaching" className="w-full aspect-video rounded-lg" src={`https://player.vimeo.com/video/${vimeo[1]}`} allowFullScreen />;
  return <video src={url} controls className="w-full rounded-lg bg-black" />;
}

export default function Library() {
  const [c, setC] = useState(null);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    portalApi.get("/portal/content/current").then((r) => { setWeek(r.data.week); setC(r.data.content); }).catch(() => {}).finally(() => setLoading(false));
    portalApi.get("/portal/recordings").then((r) => setRecs(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="portal-library" className="max-w-3xl">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">Teaching & resources</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">Library</h1>
      <p className="text-[#5E4B3C] font-body mb-10 leading-relaxed">This week's teaching, notes, and downloads. More opens as your cohort moves through the journey.</p>

      {loading ? <p className="text-[#8a7a6a] font-body">Loading…</p> : !c ? (
        <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-8 text-center">
          <BookOpen className="w-8 h-8 text-[#A9724E] mx-auto mb-3" strokeWidth={1.25} />
          <p className="font-serif-display text-2xl text-[#2A2825]">The journey opens soon.</p>
          <p className="text-[#5E4B3C] font-body mt-1">This week's teaching will appear here once your cohort begins.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8">
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-3">Week {week} · {c.movement}</p>
            <h2 className="font-serif-display text-2xl mb-4">{c.theme} — {c.practice}</h2>
            {c.teaching_video_url ? <Teaching url={c.teaching_video_url} /> : (
              <div className="rounded-lg bg-[#EAE3D9] py-12 text-center text-[#8a7a6a] font-body"><PlayCircle className="w-8 h-8 mx-auto mb-2 text-[#A9724E]" strokeWidth={1.25} />Teaching video coming for this week</div>
            )}
            {c.teaching_audio_url && (
              <div className="mt-4"><p className="flex items-center gap-2 text-xs tracking-overline uppercase text-[#8a7a6a] mb-2"><Headphones className="w-3.5 h-3.5" strokeWidth={1.5} />Audio only</p><audio src={c.teaching_audio_url} controls className="w-full" /></div>
            )}
          </div>
          {c.downloads?.length > 0 && (
            <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8">
              <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4">Downloads & resources</p>
              <div className="space-y-2">
                {c.downloads.map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#A9724E] hover:underline"><Download className="w-4 h-4" strokeWidth={1.5} />{d.title || d.url}</a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {recs.length > 0 && (
        <div className="mt-10" data-testid="library-recordings">
          <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4 flex items-center gap-2"><Video className="w-3.5 h-3.5" strokeWidth={1.5} />Recorded sessions from your home</p>
          <div className="space-y-4">
            {recs.map((r) => (
              <div key={r.id} data-testid={`library-recording-${r.id}`} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-4">
                <p className="font-serif-display text-lg text-[#2A2825] mb-2">{r.title} <span className="text-xs text-[#8a7a6a] font-body">· {r.host_name}</span></p>
                <video src={mediaUrl(r.path)} controls className="w-full rounded-lg bg-black max-h-72" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
